import { Link } from "react-router-dom";
import {
  GraduationCap,
  Building2,
  BadgeCheck,
  MapPin,
  Users,
  ChefHat,
  ArrowUpRight,
} from "lucide-react";
import { TRUST_BADGES } from "../data/content";
import { Reveal } from "./Reveal";

const ICONS = {
  GraduationCap,
  Building2,
  BadgeCheck,
  MapPin,
  Users,
  ChefHat,
};

export const TrustStrip = () => {
  return (
    <section
      id="trust-strip"
      data-testid="trust-strip"
      className="relative pt-10 sm:pt-12 lg:pt-14 pb-10 sm:pb-14 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <Reveal>
          <p className="text-center text-[15px] sm:text-[17px] lg:text-[18px] font-semibold text-brand-charcoal max-w-3xl mx-auto leading-snug">
            Trusted for school events, agency meetings, office lunches,
            community programs, and private celebrations.
          </p>
        </Reveal>
        <Reveal delay={1}>
          <div className="mt-3 flex justify-center">
            <span
              data-testid="trust-tap-hint"
              className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-4 py-2 text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.1em] shadow-[0_8px_22px_-10px_rgba(245,180,40,0.7)] animate-pulse-soft"
            >
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full bg-brand-charcoal/80"
              />
              Tap any badge to see updates &amp; photos
            </span>
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {TRUST_BADGES.map((b, i) => {
            const Icon = ICONS[b.icon] || BadgeCheck;
            return (
              <Reveal key={b.slug} delay={Math.min(i + 1, 6)}>
                <Link
                  to={`/experience/${b.slug}`}
                  data-testid={`trust-badge-${b.slug}`}
                  aria-label={`See updates and gallery for ${b.title}`}
                  className="group relative flex flex-col items-center text-center gap-2 rounded-2xl bg-brand-gray/60 border border-black/5 px-4 py-5 transition-all hover:bg-white hover:border-brand-teal/40 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal overflow-hidden"
                >
                  {/* Faded relevant background image */}
                  {b.image && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center opacity-50 group-hover:opacity-65 transition-opacity"
                      style={{ backgroundImage: `url('${b.image}')` }}
                    />
                  )}
                  {/* Subtle gradient so text stays legible */}
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/35 to-white/70 group-hover:from-white/55 group-hover:via-white/30 group-hover:to-white/65 transition-colors"
                  />
                  <span className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <ArrowUpRight className="h-3.5 w-3.5 text-brand-teal" />
                  </span>
                  <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border border-black/5 text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors shadow-sm">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="relative">
                    <p className="text-[12.5px] font-extrabold uppercase tracking-wider text-brand-charcoal drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]">
                      {b.title}
                    </p>
                    <p className="mt-0.5 text-[11px] font-medium text-brand-charcoal/75 leading-snug drop-shadow-[0_1px_2px_rgba(255,255,255,0.85)]">
                      {b.subtitle}
                    </p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustStrip;
